import React from 'react';
import { usePagination } from 'hooks/use-table-pagination.hook';
import { TableUtils } from 'pages/private/components/table-utils';
import { useAccountStore, useRaptorComponentsStore } from 'store';
import {
    AdvanceTable,
    HorizontalMenu,
    Icon,
    IMenuItem,
    notification,
    SuccessTag,
    Typography,
    useModal,
    WarningTag,
} from 'ui-lib';
import { RaptorComponentHelpers } from './helpers';
import { useLoadingStates } from 'hooks/use-loading.hook';
import { useRaptorComponentPermissionContext } from 'contexts/permissions/raptor-component-permission.context';
import { OrganisationSelect } from 'pages/private/components/organisation-select';
import { ComponentDto, ComponentType, RaptorComponentViewModel } from 'dto/raptor-component.dto';
import { RaptorComponentVersionViewModel, ComponentVersionStatus } from 'dto/raptor-component-version.dto';
import { ComponentVersionDisplayHelpers } from './version/component-version.helpers';
import css from './raptor-components.css';
import { useRaptorComponentNavigator } from '../hooks/use-raptor-component-navigator';
import componentAccountSelector from '../hooks/use-account-selector';
interface IComponentVersionsTableProps {
    accountId: number;
    accountUuid: string;
    component: ComponentDto;
    canModify: boolean;
}

const ComponentVersionsTable = (props: IComponentVersionsTableProps) => {
    const { accountId, accountUuid, component, canModify } = props;
    const componentUuid = component.uuid;
    const componentName = component.displayName;
    const [modal, contextHolder] = useModal();
    const raptorComponentsStore = useRaptorComponentsStore();
    const { withSaving } = useLoadingStates();
    const navigation = useRaptorComponentNavigator();
    const { pagination, data, loading, refresh, onTableChange } = usePagination({
        pageSize: 20,
        fetchData: async (params) => {
            const { pageSize, page } = params;
            const offset = (page - 1) * pageSize;
            const result = await raptorComponentsStore.listComponentVersions(accountId, componentUuid, {
                offset,
                limit: pageSize,
                includeManifests: true,
            });

            return {
                data: result.map((componentVersion) => new RaptorComponentVersionViewModel(componentVersion, accountUuid)),
            };
        },
        enableShowTotal: true,
        cacheKey: `component-versions-table-${componentUuid}`,
        mode: 'offset',
    });

    const showComponentVersionDetails = (record: RaptorComponentVersionViewModel): void => {
        RaptorComponentHelpers.showComponentVersionDetailsModal(modal, {
            accountId,
            componentVersionUuid: record.uuid,
        });
    };

    const buildComponentVersionActionMenuItems = (record: RaptorComponentVersionViewModel): IMenuItem[] => {
        if (!canModify) {
            return [];
        }
        return [
            {
                code: 'actionsMenu',
                label: () => 'Settings',
                subMenuItems: [
                    {
                        code: 'edit',
                        label: () => 'Edit',
                        iconName: 'pen',
                        onClick: () => {
                            navigation.toComponentVersionEditingPage({
                                componentUuid,
                                versionUuid: record.uuid,
                                accountUuid: accountUuid,
                            });
                        },
                    },
                    {
                        code: 'publish',
                        label: () => 'Publish',
                        iconName: 'cloud-arrow-up',
                        onClick: () => {
                            const componentVersionDisplayName =
                                ComponentVersionDisplayHelpers.getComponentVersionDisplayName(
                                    componentName,
                                    record.version,
                                );
                            RaptorComponentHelpers.showPublishComponentVersionConfirmDialog({
                                componentName,
                                componentVersion: record.version,
                                action: async () => {
                                    const result = await withSaving(async () => {
                                        return raptorComponentsStore.publishComponentVersion(accountId, record.uuid);
                                    });

                                    if (result.isSuccess()) {
                                        notification.success({
                                            message: `Component ${componentVersionDisplayName} published successfully`,
                                        });
                                        return refresh();
                                    }

                                    notification.error({
                                        message: `Failed to publish component ${componentVersionDisplayName}`,
                                        description: result.errorMessage,
                                    });
                                },
                            });
                        },
                    },
                    {
                        code: 'delete',
                        label: () => 'Delete',
                        iconName: 'trash',
                        onClick: () => {
                            const componentVersionDisplayName =
                                ComponentVersionDisplayHelpers.getComponentVersionDisplayName(
                                    componentName,
                                    record.version,
                                );
                            RaptorComponentHelpers.showDeleteComponentVersionDialog({
                                componentName,
                                componentVersion: record.version,
                                action: async () => {
                                    const result = await withSaving(async () => {
                                        return raptorComponentsStore.deleteComponentVersion(accountId, record.uuid);
                                    });

                                    if (result.isSuccess()) {
                                        notification.success({
                                            message: `Component ${componentVersionDisplayName} deleted successfully`,
                                        });
                                        return refresh();
                                    }

                                    notification.error({
                                        message: `Failed to delete component ${componentVersionDisplayName}`,
                                        description: result.errorMessage,
                                    });
                                },
                            });
                        },
                    },
                ],
                onClick: () => undefined,
            },
        ];
    };

    return (
        <React.Fragment>
            {contextHolder}
            <AdvanceTable
                title='Versions'
                toolbar={{
                    onReload: refresh,
                }}
                loadingState={loading}
                table={{
                    rowKey: 'uuid',
                    pagination,
                    onTableChange,
                    columns: [
                        TableUtils.createBasicFilterColumn({
                            key: 'version',
                            title: 'Version',
                            render: (text, record: RaptorComponentVersionViewModel) => (
                                <Typography.Link onClick={() => showComponentVersionDetails(record)}>
                                    {text}
                                </Typography.Link>
                            ),
                        }),
                        TableUtils.createTruncatedTextColumn({ key: 'description', title: 'Description' }),
                        TableUtils.createBasicEnumFilterColumn({
                            key: 'status',
                            title: 'Status',
                            filters: Object.values(ComponentVersionStatus).map((item) => ({ value: item, text: item })),
                            columnExtraOptions: {
                                render: (text) => {
                                    switch (text) {
                                        case ComponentVersionStatus.PUBLISHED:
                                            return <SuccessTag value={text} />;
                                        default:
                                            return <WarningTag value={text} />;
                                    }
                                },
                            },
                        }),
                        TableUtils.createBasicFilterTimeColumn({ key: 'updatedAt', title: 'Updated At' }),
                        TableUtils.createExtraColumn({
                            key: 'actions',
                            title: 'Actions',
                            render: (_, record: RaptorComponentVersionViewModel) => {
                                if (record.status === ComponentVersionStatus.PUBLISHED) {
                                    return null;
                                }
                                const items = buildComponentVersionActionMenuItems(record);
                                return (
                                    <div className={css['table-actions']}>
                                        {items.length > 0 && <HorizontalMenu menuItems={items} />}
                                    </div>
                                );
                            },
                        }),
                    ],
                    dataSource: data,
                }}
            />
        </React.Fragment>
    );
};

export const RaptorComponentsPage = () => {
    const [modal, contextHolder] = useModal();
    const raptorComponentsStore = useRaptorComponentsStore();
    const accountStore = useAccountStore();
    const loadingHook = useLoadingStates();
    const permissionContext = useRaptorComponentPermissionContext();
    const navigation = useRaptorComponentNavigator();
    const accountSelector = componentAccountSelector();
    const {
        pagination,
        data: components,
        loading,
        refresh,
        onTableChange,
    } = usePagination({
        pageSize: 100,
        fetchData: async (params) => {
            const selectedAccount = accountSelector.selectedAccount;
            if (!selectedAccount) {
                return {
                    data: [],
                };
            }
            const { pageSize, page } = params;
            const offset = (page - 1) * pageSize;
            const result = await raptorComponentsStore.getComponents(selectedAccount.id, {
                accountUuid: selectedAccount.uuid,
                offset,
                limit: pageSize,
            });

            return {
                data: result.map((component) => new RaptorComponentViewModel(component, accountStore)),
            };
        },
        enableShowTotal: true,
        cacheKey: `components-table`,
        mode: 'offset',
    });

    React.useEffect(() => {
        refresh();
    }, [accountSelector.selectedAccount]);

    const buildComponentActionMenuItems = (record: RaptorComponentViewModel): IMenuItem[] => {
        const subMenuItems: IMenuItem[] = [];

        if (!permissionContext.canModifyComponent(record)) {
            return [];
        }
        subMenuItems.push({
            code: 'create-version',
            label: () => 'Create version',
            iconName: 'plus',
            onClick: () => {
                navigation.toComponentVersionCreationPage({
                    componentUuid: record.uuid,
                    accountUuid: record.accountUuid,
                });
            },
        });
        subMenuItems.push({
            code: 'delete',
            label: () => 'Delete',
            iconName: 'trash',
            onClick: () => {
                RaptorComponentHelpers.showDeleteComponentDialog({
                    componentName: record.displayName,
                    action: async (force?: boolean) => {
                        const result = await loadingHook.withSaving(async () => {
                            return raptorComponentsStore.deleteComponent(
                                accountSelector.selectedAccount.id,
                                record.uuid,
                                {
                                    force,
                                },
                            );
                        });
                        if (result.isSuccess()) {
                            notification.success({
                                message: `Component ${record.displayName} deleted successfully`,
                            });
                            return refresh();
                        }
                        notification.error({
                            message: `Failed to delete component ${record.displayName}`,
                            description: result.errorMessage,
                        });
                    },
                });
            },
        });
        return [
            {
                code: 'actionsMenu',
                label: () => 'Settings',
                subMenuItems,
                onClick: () => undefined,
            },
        ];
    };

    return (
        <React.Fragment>
            {contextHolder}
            <AdvanceTable
                title='Raptor Components'
                loadingState={loading}
                toolbar={{
                    extraItems:
                        accountSelector.isDPUAdmin
                            ? [
                                  <OrganisationSelect
                                      key='Organisation'
                                      placeholder='Organisation'
                                      disabled={loading}
                                      treeDataSource={accountSelector.dataSource}
                                      filterTreeNode={(input, treeNode) => {
                                          return treeNode.label?.includes(input);
                                      }}
                                      expandAll={true}
                                      onChange={accountSelector.onChange}
                                      value={accountSelector.selectedAccount?.id}
                                  />,
                              ]
                            : [],
                    onReload: refresh,
                    onAdd: permissionContext.canModify
                        ? () =>
                              navigation.toComponentCreationPage({ accountUuid: accountSelector.selectedAccount?.uuid })
                        : undefined,
                }}
                table={{
                    rowKey: 'uuid',
                    pagination,
                    onTableChange,
                    columns: [
                        TableUtils.createBasicFilterColumn({
                            key: 'displayName',
                            title: 'Name',
                        }),
                        TableUtils.createTruncatedTextColumn({ key: 'description', title: 'Description' }),
                        TableUtils.createBasicEnumFilterColumn({
                            key: 'type',
                            filters: Object.values(ComponentType).map((item) => ({ value: item, text: item })),
                            title: 'Type',
                        }),
                        TableUtils.createBasicFilterColumn({ key: 'latestVersion', title: 'Latest Published Version' }),
                        TableUtils.createBasicFilterColumn({
                            key: 'accountName',
                            title: 'Account Name',
                        }),
                        TableUtils.createBasicFilterTimeColumn({ key: 'updatedAt', title: 'Last Updated At' }),
                        TableUtils.createExtraColumn({
                            key: 'actions',
                            title: 'Actions',
                            render: (_, record: RaptorComponentViewModel) => {
                                const menuItems = buildComponentActionMenuItems(record);
                                return (
                                    <div className={css['table-actions']}>
                                        {menuItems.length > 0 && <HorizontalMenu menuItems={menuItems} />}
                                    </div>
                                );
                            },
                        }),
                    ],
                    expandable: {
                        expandedRowRender: (record: any, index: number, indent: number, expanded: boolean) => (
                            <ComponentVersionsTable
                                component={record}
                                canModify={permissionContext.canModifyComponent(record)}
                                accountUuid={record.accountUuid ?? accountSelector.selectedAccount.uuid}
                                accountId={accountSelector.selectedAccount.id}
                            />
                        ),
                        expandIcon: ({ expanded, onExpand, record }) =>
                            expanded ? (
                                <Icon iconName={'angle-down'} size={'LG'} onClick={(e) => onExpand(record, e)} />
                            ) : (
                                <Icon iconName={'angle-right'} size={'LG'} onClick={(e) => onExpand(record, e)} />
                            ),
                    },
                    dataSource: components,
                }}
            />
        </React.Fragment>
    );
};
